import { apiUrl } from './api_config'

const API_BASE_URL = apiUrl('/books')
const GET_PUBLISHED_BOOKS_ENDPOINT = `${API_BASE_URL}/all`
const GET_BOOK_TITLES_ENDPOINT = `${API_BASE_URL}/titles`
const GET_BOOK_AUTHORS_ENDPOINT = `${API_BASE_URL}/authors`
const GET_BOOK_ENDPOINT = `${API_BASE_URL}/getBook`
const ADD_BOOK_ENDPOINT = `${API_BASE_URL}/add`
const UPDATE_BOOK_ENDPOINT = `${API_BASE_URL}/updateBook`
const DELETE_BOOK_ENDPOINT = `${API_BASE_URL}/deleteBook`

export const BOOKS_PAGE_SIZE = 20

async function request(url, options = {}) {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  })

  if (!response.ok) {
    let message = `Book request failed (${response.status})`
    try {
      const error = await response.json()
      message = error.message || error.error || message
    } catch {
      // Keep the HTTP status message when the server does not return JSON.
    }
    throw new Error(message)
  }

  if (response.status === 204) return null
  return response.json()
}

function findBookList(data) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []

  for (const key of ['books', 'bookList', 'results', 'items', 'data']) {
    const list = findBookList(data[key])
    if (list.length || Array.isArray(data[key])) return list
  }

  return []
}

function findNextCursor(data) {
  if (!data || typeof data !== 'object') return null
  return (
    data.nextCursor ??
    data.next_cursor ??
    data.nextPageToken ??
    data.next_page_token ??
    data.next ??
    findNextCursor(data.books) ??
    findNextCursor(data.bookList) ??
    findNextCursor(data.results) ??
    findNextCursor(data.items) ??
    findNextCursor(data.data) ??
    findNextCursor(data.pagination) ??
    findNextCursor(data.meta) ??
    null
  )
}

export async function getPublishedBooks({
  cursor = null,
  page = 1,
  search = '',
  title = '',
  type = '',
  signal,
} = {}) {
  const params = new URLSearchParams({ limit: String(BOOKS_PAGE_SIZE) })
  if (cursor) params.set('cursor', cursor)
  else params.set('page', String(page))
  if (search.trim()) params.set('search', search.trim())
  if (title.trim()) params.set('title', title.trim())
  if (type) params.set('type', type)

  const data = await request(
    `${GET_PUBLISHED_BOOKS_ENDPOINT}?${params.toString()}`,
    { signal },
  )
  const payload = data?.data && !Array.isArray(data.data) ? data.data : data
  const books = findBookList(data)
  const nextCursor = findNextCursor(data)
  const currentPage = Number(
    payload?.page ?? payload?.currentPage ?? payload?.current_page ?? page,
  )
  const totalPages = Number(payload?.totalPages ?? payload?.total_pages ?? 0)
  const hasNext = Boolean(
    payload?.hasNext ??
    payload?.has_next ??
    nextCursor ??
    totalPages > currentPage,
  )

  return {
    books,
    nextCursor,
    hasNext,
    currentPage,
    nextPage: hasNext && !nextCursor ? currentPage + 1 : null,
  }
}

export async function getBookTitles(type = '') {
  const query = type ? `?type=${encodeURIComponent(type)}` : ''
  const data = await request(`${GET_BOOK_TITLES_ENDPOINT}${query}`)
  return findBookList(data)
}

export async function getBookAuthors() {
  const data = await request(GET_BOOK_AUTHORS_ENDPOINT)
  return Array.isArray(data?.data) ? data.data : []
}

export async function getBook(id) {
  const data = await request(`${GET_BOOK_ENDPOINT}/${encodeURIComponent(id)}`)
  const payload = data?.data && !Array.isArray(data.data) ? data.data : data
  return payload?.book || payload || {}
}

export async function createBook(book) {
  const data = await request(ADD_BOOK_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify(book),
  })
  return data?.data ?? data
}

export async function updateBook(id, book) {
  const data = await request(
    `${UPDATE_BOOK_ENDPOINT}/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(book) },
  )
  return data?.data ?? data
}

export async function getBookRoyalties(bookId) {
  const data = await request(
    `${API_BASE_URL}/${encodeURIComponent(bookId)}/royalties`,
  )
  return Array.isArray(data?.data) ? data.data : []
}

export async function updateBookRoyalty(bookAuthorId, payload) {
  const data = await request(
    `${API_BASE_URL}/royalties/${encodeURIComponent(bookAuthorId)}`,
    { method: 'PUT', body: JSON.stringify(payload) },
  )
  return data?.data ?? data
}

export async function deleteBookRoyalty(bookAuthorId) {
  return request(
    `${API_BASE_URL}/royalties/${encodeURIComponent(bookAuthorId)}`,
    {
      method: 'DELETE',
    },
  )
}

export async function getBookPrices(bookId) {
  const data = await request(
    `${API_BASE_URL}/${encodeURIComponent(bookId)}/prices`,
  )
  return Array.isArray(data?.data) ? data.data : []
}

export async function updateBookPrice(priceId, unitprice) {
  const data = await request(
    `${API_BASE_URL}/prices/${encodeURIComponent(priceId)}`,
    { method: 'PUT', body: JSON.stringify({ unitprice }) },
  )
  return data?.data ?? data
}

export async function deleteBook(id) {
  return request(`${DELETE_BOOK_ENDPOINT}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}
